import type { ServiceOffering } from "@/lib/service/types";

export type ServiceOfferingDocSummary = {
  docCount: number;
  hasOrderForm: boolean;
};

export type ServiceOfferingReadiness = {
  rigCount: number;
  techCount: number;
  hasCrew: boolean;
  hasEquipment: boolean;
  hasOrderForm: boolean;
  isQuotable: boolean;
  /** Crew + equipment + order form — quotable is informational only. */
  dispatchReady: boolean;
  blockingDone: number;
  blockingTotal: number;
};

export function countLinkedRigs(
  offeringId: string,
  rigOfferingIds: Record<string, string[]>
): number {
  return Object.entries(rigOfferingIds).filter(([, ids]) => ids.includes(offeringId)).length;
}

export function countLinkedTechs(
  offeringId: string,
  techOfferingIds: Record<string, string[]>
): number {
  return Object.entries(techOfferingIds).filter(([, ids]) => ids.includes(offeringId)).length;
}

export function computeOfferingReadiness(args: {
  offering: ServiceOffering;
  rigOfferingIds: Record<string, string[]>;
  techOfferingIds: Record<string, string[]>;
  docSummary?: ServiceOfferingDocSummary;
}): ServiceOfferingReadiness {
  const rigCount = countLinkedRigs(args.offering.id, args.rigOfferingIds);
  const techCount = countLinkedTechs(args.offering.id, args.techOfferingIds);
  const hasCrew = techCount >= 1;
  const hasEquipment = rigCount >= 1;
  const hasOrderForm = args.docSummary?.hasOrderForm ?? false;
  const isQuotable = args.offering.quotable_from_sales;
  const blocking = [hasCrew, hasEquipment, hasOrderForm];
  const blockingDone = blocking.filter(Boolean).length;

  return {
    rigCount,
    techCount,
    hasCrew,
    hasEquipment,
    hasOrderForm,
    isQuotable,
    dispatchReady: blockingDone === blocking.length,
    blockingDone,
    blockingTotal: blocking.length,
  };
}

export function readinessBadgeLabel(readiness: ServiceOfferingReadiness): string {
  if (readiness.dispatchReady) return "Ready for dispatch";
  if (readiness.blockingDone === 0) return "Needs setup";
  return `${readiness.blockingDone}/${readiness.blockingTotal} ready`;
}

export function readinessSummaryLine(
  readiness: ServiceOfferingReadiness,
  docCount: number
): string {
  const parts = [
    `${readiness.rigCount} rig${readiness.rigCount === 1 ? "" : "s"}`,
    `${readiness.techCount} tech${readiness.techCount === 1 ? "" : "s"}`,
    readiness.isQuotable ? "Quotable" : "Not quotable",
    `${docCount} doc${docCount === 1 ? "" : "s"}`,
  ];
  return parts.join(" · ");
}

export const READINESS_CHECKLIST = [
  { key: "crew" as const, label: "Crew assigned", blocking: true },
  { key: "equipment" as const, label: "Equipment linked", blocking: true },
  { key: "orderForm" as const, label: "Order form uploaded", blocking: true },
  { key: "quotable" as const, label: "Quotable from sales", blocking: false },
] as const;

export function checklistItemDone(
  key: (typeof READINESS_CHECKLIST)[number]["key"],
  readiness: ServiceOfferingReadiness
): boolean {
  switch (key) {
    case "crew":
      return readiness.hasCrew;
    case "equipment":
      return readiness.hasEquipment;
    case "orderForm":
      return readiness.hasOrderForm;
    case "quotable":
      return readiness.isQuotable;
  }
}
